import _ from "lodash";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { z, ZodSchema } from "zod";

export const useURLState = <T extends ZodSchema>(
  {
    path,
    schema,
    initValue,
    onError,
  }: {
    path: string;
    schema: T;
    initValue: z.infer<T>;
    onError?: (err: any) => any;
  },
  deps: any[]
): {
  data: z.infer<T> | null;
  error: any;
  setValue: (v: z.infer<T>) => void;
} => {
  /*

  Description:
    The job of this hook is similar to useState, except that it
    stores the state in the URL query params instead of memory.
    
    Since the data can be inserted into this state variable directly
    by the user, the data needs to be validated before being inserted
    into memory.

  Params:
    - path
    - schema
    - initValue
    - onError

  Returns:
    - currentValue
    - error
    - setter

  @todo: Handle cases for URL length exceeding

  */

  const navigate = useNavigate();

  const { search } = useLocation();
  const [data, error] = useMemo(() => {
    try {
      const rawD = search && queryStringToObject(search.slice(1) || "");

      if (!rawD[path]) return [initValue, null];

      const data = schema.parse(rawD[path]);

      return [data, null];
    } catch (err) {
      if (typeof onError === "function") onError(err);
      return [null, err];
    }
  }, [search, path, ...deps]);

  const setValue = useCallback(
    (v: z.infer<T>) => {
      const queryStringPartial = objectToQueryString({ [path]: v });
      const newQueryString = `${queryStringPartial}`;

      navigate({ search: newQueryString });
    },
    [navigate, path]
  );

  return { data, error, setValue };
};

export function objectToQueryString(obj: any, prefix: string = ""): string {
  const queryStringParts: string[] = [];

  for (const key in obj) {
    const value = obj[key];
    const fullKey = prefix ? `${prefix}[${key}]` : key;

    if (
      typeof value === "object" &&
      value !== null &&
      !(value instanceof Date)
    ) {
      // Handle nested objects or arrays recursively
      queryStringParts.push(objectToQueryString(value, fullKey));
    } else if (value !== undefined) {
      const stringifiedValue =
        value instanceof Date ? value.toISOString() : String(value);
      // Handle primitive values
      queryStringParts.push(
        `${encodeURIComponent(fullKey)}=${encodeURIComponent(stringifiedValue)}`
      );
    }
  }

  return queryStringParts.join("&");
}

export function queryStringToObject(queryString: string): any {
  const obj: any = {};

  queryString.split("&").forEach((pair) => {
    const [rawKey, value] = pair.split("=");
    const decodedKey = decodeURIComponent(rawKey);
    const decodedValue = decodeURIComponent(value || "");

    const keys = decodedKey.split(/[\[\]]+/).filter((k) => k !== "");

    let current = obj;
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];

      if (i === keys.length - 1) {
        // Set the value on the last key
        if (Array.isArray(current[key])) {
          current[key].push(decodedValue);
        } else if (current[key] !== undefined) {
          current[key] = [current[key], decodedValue];
        } else {
          current[key] = decodedValue;
        }
      } else {
        // Create nested object or array if necessary
        if (!current[key]) {
          current[key] = isNaN(parseInt(keys[i + 1], 10)) ? {} : [];
        }
        current = current[key];
      }
    }
  });

  return obj;
}
